import React, { useCallback, useMemo, useState } from 'react';
import {
    Body,
    Button,
    Card,
    CardBody,
    Input,
    Modal,
    ModalBody,
    ModalHeader,
    ModalFooter,
} from '@panwds/react-ui';
import { EnvelopeIcon, SearchIcon } from '@panwds/icons';
import { twMerge } from '@panwds/tailwindcss';
import { subscribe } from '../../services/api/subscriptions';
import { getEnabledPlanes } from '../../shared/utils/products';
import useModalBackdropClose from '../../shared/hooks/useModalBackdropClose';
import useRegionTreeItems from './hooks/useRegionTreeItems';
import SubscriptionTypesSection from './components/SubscriptionTypesSection';
import AddProductSelect from './components/AddProductSelect';
import SubscribedProductPanel from './components/SubscribedProductPanel';
import EventSubscriptionsSection from './components/EventSubscriptionsSection';
import ConfirmModal from './components/ConfirmModal';
import { buildEventTypePreferences, DEFAULT_SUBSCRIPTION_TYPES } from './utils/subscriptionTypes';
import { buildSubscriptionPayload } from './utils/subscriptionPayload';
import { serializeSubscriptionState } from './utils/subscriptionDiff';
import { isSessionValid, getSessionToken, clearSession } from './utils/session';
import { largeModalStyle, MODAL_BORDER_CLASS } from './utils/modalLayout';
import { BORDERED_CARD_CLASS } from './utils/cardStyles';

const getSubscribedProductTypes = (checkedProducts) => (
    Object.keys(checkedProducts).filter((cp) => checkedProducts[cp]?.checked)
);

const ManageSubscriptionsModal = ({
    isOpen,
    onClose,
    products,
    checkedProducts,
    setCheckedProducts,
    baselineCheckedProducts = {},
    subscriptionTypes,
    setSubscriptionTypes,
    baselineSubscriptionTypes = DEFAULT_SUBSCRIPTION_TYPES,
    expandedProducts,
    toggleExpandedRegions,
    subscribedIncidents,
    subscribedMaintenances,
    subscribedAdvisories,
    toast,
    clear,
    onSaveSuccess,
}) => {
    const [productFilter, setProductFilter] = useState('');
    const [openProducts, setOpenProducts] = useState(() => {
        const [firstSubscribed] = getSubscribedProductTypes(checkedProducts);
        return firstSubscribed ? { [firstSubscribed]: true } : {};
    });
    // Which product the user has asked to remove, held until they confirm. `null` means
    // no removal is pending.
    const [pendingRemovalProduct, setPendingRemovalProduct] = useState(null);
    const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

    const { getRegionItems, getLeafValues } = useRegionTreeItems(products);

    const subscriberEmail = localStorage.getItem('email') || '';
    const subscribedProductTypes = useMemo(
        () => getSubscribedProductTypes(checkedProducts),
        [checkedProducts],
    );

    // "Dirty" is payload inequality against the last known server state, not raw state
    // inequality -- see `serializeSubscriptionState` for why that distinction matters.
    const isDirty = useMemo(
        () => serializeSubscriptionState(checkedProducts, subscriptionTypes, products)
            !== serializeSubscriptionState(baselineCheckedProducts, baselineSubscriptionTypes, products),
        [checkedProducts, subscriptionTypes, baselineCheckedProducts, baselineSubscriptionTypes, products],
    );

    // Single guarded exit used by every dismissal path (footer Cancel, header x, Escape,
    // backdrop click) so unsaved edits cannot be lost through a side door.
    const requestClose = useCallback(() => {
        if (isDirty) {
            setIsDiscardConfirmOpen(true);
            return;
        }
        onClose();
    }, [isDirty, onClose]);

    useModalBackdropClose(isOpen, requestClose);

    const visibleProducts = useMemo(() => {
        const subscribed = products.filter((p) => checkedProducts[p.product_type]?.checked);
        const term = productFilter.trim().toLowerCase();
        if (!term) return subscribed;
        return subscribed.filter((p) => p.product_type?.toLowerCase().includes(term));
    }, [products, checkedProducts, productFilter]);

    const toggleOpenProduct = (productType) => {
        setOpenProducts((prev) => ({ ...prev, [productType]: !prev[productType] }));
    };

    // "Expand All" flips to "Collapse All" once every *visible* (filter-respecting) panel
    // is already open, so the control always describes the action it will perform.
    const areAllVisibleOpen = visibleProducts.length > 0
        && visibleProducts.every((p) => openProducts[p.product_type]);

    const toggleAllVisible = () => {
        setOpenProducts((prev) => {
            const next = { ...prev };
            visibleProducts.forEach((p) => { next[p.product_type] = !areAllVisibleOpen; });
            return next;
        });
    };

    // Builds the initial `checkedProducts` entry for a newly added product, pre-selecting
    // every available region in each enabled plane (per user decision -- a freshly added
    // product should be immediately savable without an extra "pick regions" step).
    const buildAllRegionsEntry = (productType, enabledPlanes) => {
        const multiPlane = enabledPlanes.length >= 2;
        if (multiPlane) {
            const entry = { checked: true, regions: [], control: [], data: [] };
            enabledPlanes.forEach(([planeType]) => {
                entry[planeType] = getLeafValues(productType, planeType);
            });
            return entry;
        }
        const allRegions = getLeafValues(productType);
        return { checked: true, regions: allRegions, control: [], data: [] };
    };

    // Shared by the "+ Add Product" picker and the per-product remove `x` -- both act on
    // the same `checkedProducts` membership, so a single diff-based handler keeps them
    // from drifting. `nextProductTypes` is the full next set of checked product types
    // (as reported by AddProductSelect's TreeSelect `onChange`).
    const handleChangeProducts = (nextProductTypes) => {
        const nextSet = new Set(nextProductTypes);
        const added = nextProductTypes.filter((pt) => !checkedProducts[pt]?.checked);
        const removed = subscribedProductTypes.filter((pt) => !nextSet.has(pt));
        if (!added.length && !removed.length) return;

        setCheckedProducts((prev) => {
            const next = { ...prev };
            added.forEach((productType) => {
                const product = products.find((p) => p.product_type === productType);
                next[productType] = buildAllRegionsEntry(productType, getEnabledPlanes(product));
            });
            removed.forEach((productType) => {
                delete next[productType];
            });
            return next;
        });

        setOpenProducts((prev) => {
            const next = { ...prev };
            added.forEach((productType) => { next[productType] = true; });
            removed.forEach((productType) => { delete next[productType]; });
            return next;
        });
    };

    const handleRemoveProduct = (productType) => {
        setCheckedProducts((prev) => {
            const next = { ...prev };
            delete next[productType];
            return next;
        });
        setOpenProducts((prev) => {
            const next = { ...prev };
            delete next[productType];
            return next;
        });
    };

    // `planeOrArrayKey` mirrors the previous `handleProductCheckChange`'s `regionType`
    // param: the actual plane key (`control`/`data`) the change applies to. `regions` is
    // always kept in sync too, since it remains the source of truth for single-plane
    // products (see `buildSubscriptionPayload`); the mirrored plane key is otherwise
    // unused for single-plane products but kept for parity with subscription hydration.
    const handleChangeRegions = (productType, planeOrArrayKey, selectedItems) => {
        setCheckedProducts((prev) => ({
            ...prev,
            [productType]: {
                ...prev[productType],
                checked: true,
                regions: selectedItems || [],
                ...(planeOrArrayKey ? { [planeOrArrayKey]: selectedItems || [] } : {}),
            },
        }));
    };

    // Unlike `handleChangeRegions`, a single-chip removal only ever touches the one
    // array it came from (`regions` for single-plane products, or the specific plane key
    // for multi-plane products) -- it must not also overwrite `regions` for multi-plane
    // products, or removing one plane's chip would silently wipe the other plane's state.
    const handleRemoveRegion = (productType, arrayKey, value) => {
        setCheckedProducts((prev) => {
            const current = prev[productType] || {};
            const nextValues = (current[arrayKey] || []).filter((v) => v !== value);
            return {
                ...prev,
                [productType]: {
                    ...current,
                    checked: true,
                    [arrayKey]: nextValues,
                },
            };
        });
    };

    const handleSave = async () => {
        if (isSessionValid()) {
            try {
                const payload = buildSubscriptionPayload(checkedProducts, products);
                const saveSubs = await subscribe({
                    email: localStorage.getItem('email'),
                    session_token: getSessionToken(),
                    subscription: payload,
                    ...buildEventTypePreferences(subscriptionTypes),
                });
                toast(saveSubs.message, { appearance: saveSubs?.status });
                setTimeout(() => clear(), 5000);
                await onSaveSuccess?.();
                onClose();
            } catch (error) {
                console.error('Error saving subscriptions:', error);
                toast('Failed to save subscriptions.', { appearance: 'error' });
                setTimeout(() => clear(), 5000);
            }
        } else {
            clearSession();
            toast('Session expired. Please sign in again.', { appearance: 'warning' });
            setTimeout(() => clear(), 5000);
            onClose();
        }
    };

    return (
        <>
        <Modal
            onClose={requestClose}
            isOpen={isOpen}
            addClassName={twMerge('ssmutw-mt-2 lg:ssmutw-mt-[50px] ssmutw-bg-[var(--ssmu-page-bg-primary)]', MODAL_BORDER_CLASS)}
            style={largeModalStyle}
            eventsToClose={{ enableClickOutside: true, enableEscapeKey: true }}
        >
            <ModalHeader
                title="Edit Subscription"
                actions={
                    <div className="ssmutw-flex ssmutw-items-center ssmutw-gap-2 ssmutw-mr-2">
                        {subscriberEmail && (
                            <div className="ssmutw-flex ssmutw-items-center ssmutw-gap-2 ssmutw-rounded ssmutw-px-2 ssmutw-h-8 ssmutw-max-w-[260px] ssmutw-border ssmutw-border-solid ssmutw-border-ssmu-card-border">
                                <EnvelopeIcon size="sm" aria-hidden="true" className="ssmutw-text-ssmu-icon-secondary" />
                                <Body size="sm" as="span" appearance="secondary">Email &middot;</Body>
                                <Body size="sm" as="span" addClassName="ssmutw-truncate">{subscriberEmail}</Body>
                            </div>
                        )}
                    </div>
                }
            />
            <ModalBody addClassName="ssmutw-overflow-y-auto ssmutw-max-h-[calc(100vh-160px)] lg:ssmutw-max-h-[700px]">
                <SubscriptionTypesSection
                    subscriptionTypes={subscriptionTypes}
                    setSubscriptionTypes={setSubscriptionTypes}
                />

                {/*
                  * Single toolbar row per the mock: heading + Expand All on the left,
                  * filter + Add Product pushed right by `ml-auto`. Collapses to a stack
                  * below `md` so the filter input keeps its width on mobile.
                  */}
                <div className="ssmutw-flex ssmutw-flex-col ssmutw-gap-2 ssmutw-pb-3 md:ssmutw-flex-row md:ssmutw-items-center">
                    <div className="ssmutw-flex ssmutw-items-center ssmutw-gap-2">
                        <Body size="sm" as="h3" appearance="secondary" addClassName="ssmutw-font-semibold">Products And Regions</Body>
                        {visibleProducts.length > 0 && (
                            <Button
                                appearance="clear"
                                size="sm"
                                onClick={toggleAllVisible}
                                dataTestId="manage-toggle-all-products"
                            >
                                {areAllVisibleOpen ? 'Collapse All' : 'Expand All'}
                            </Button>
                        )}
                    </div>
                    <div className="ssmutw-flex ssmutw-flex-col ssmutw-gap-2 md:ssmutw-flex-row md:ssmutw-items-center md:ssmutw-ml-auto">
                        <div className="ssmutw-w-full md:ssmutw-w-[280px]">
                            <Input
                                value={productFilter}
                                aria-label="Filter products"
                                placeholder="Filter Products"
                                prefix={<SearchIcon size="sm" aria-hidden="true" className="ssmutw-text-ssmu-icon-secondary" />}
                                customClassName={{ root: 'ssmutw-pl-2' }}
                                onChange={(ev) => setProductFilter(ev?.currentTarget?.value || '')}
                            />
                        </div>
                        <AddProductSelect
                            products={products}
                            subscribedProductTypes={subscribedProductTypes}
                            onChangeProducts={handleChangeProducts}
                        />
                    </div>
                </div>

                {!subscribedProductTypes.length && (
                    <Card shadow={false} addClassName={`ssmutw-mt-4 ${BORDERED_CARD_CLASS}`}>
                        <CardBody>
                            <Body size="sm" appearance="secondary">
                                No products subscribed yet -- use Add Product to get started.
                            </Body>
                        </CardBody>
                    </Card>
                )}
                {subscribedProductTypes.length > 0 && !visibleProducts.length && (
                    <Body size="sm" appearance="secondary" addClassName="ssmutw-italic ssmutw-mt-4">
                        No subscribed products match &ldquo;{productFilter}&rdquo;.
                    </Body>
                )}
                {visibleProducts.map((p) => (
                    <SubscribedProductPanel
                        key={p.product_type}
                        product={p}
                        enabledPlanes={getEnabledPlanes(p)}
                        checkedEntry={checkedProducts[p.product_type]}
                        isOpen={!!openProducts[p.product_type]}
                        onToggleOpen={() => toggleOpenProduct(p.product_type)}
                        onRemoveProduct={() => setPendingRemovalProduct(p.product_type)}
                        getRegionItems={getRegionItems}
                        expandedProducts={expandedProducts}
                        toggleExpandedRegions={toggleExpandedRegions}
                        onChangeRegions={(planeOrArrayKey, selectedItems) => (
                            handleChangeRegions(p.product_type, planeOrArrayKey, selectedItems)
                        )}
                        onRemoveRegion={(arrayKey, value) => handleRemoveRegion(p.product_type, arrayKey, value)}
                    />
                ))}

                <EventSubscriptionsSection
                    subscribedIncidents={subscribedIncidents}
                    subscribedMaintenances={subscribedMaintenances}
                    subscribedAdvisories={subscribedAdvisories}
                    expandedEvents={expandedProducts}
                    onToggleExpand={toggleExpandedRegions}
                    onNavigate={onClose}
                />
            </ModalBody>
            <ModalFooter>
                <Button onClick={requestClose} appearance="secondary" dataTestId="manage-cancel">
                    Cancel
                </Button>
                <Button
                    appearance="primary"
                    onClick={handleSave}
                    disabled={!isDirty}
                    dataTestId="manage-save-changes"
                >
                    Save Changes
                </Button>
            </ModalFooter>
        </Modal>

        {pendingRemovalProduct && (
            <ConfirmModal
                title="Remove product subscription?"
                prompt={`This will remove your subscription to ${pendingRemovalProduct} and all selected regions. Notifications will stop once you save your changes.`}
                confirmLabel="Remove"
                cancelLabel="Cancel"
                dataTestId="manage-remove-product-confirm"
                onCancel={() => setPendingRemovalProduct(null)}
                onConfirm={() => {
                    handleRemoveProduct(pendingRemovalProduct);
                    setPendingRemovalProduct(null);
                }}
            />
        )}

        {isDiscardConfirmOpen && (
            <ConfirmModal
                title="Discard unsaved changes?"
                prompt="Are you sure you want to discard your subscription changes?"
                confirmLabel="Discard"
                cancelLabel="Keep Editing"
                dataTestId="manage-discard-confirm"
                onCancel={() => setIsDiscardConfirmOpen(false)}
                onConfirm={() => {
                    setIsDiscardConfirmOpen(false);
                    onClose();
                }}
            />
        )}
        </>
    );
};

export default React.memo(ManageSubscriptionsModal);
